// External modules
import React from 'react';
import { isEmpty, cloneDeep } from 'lodash';

import FileCopyIcon from '@material-ui/icons/FileCopyRounded';
import TextField from '@material-ui/core/TextField';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import InputAdornment from '@material-ui/core/InputAdornment';
import MenuItem from '@material-ui/core/MenuItem';

import ACDialog from '../../atoms/ACDialog';
import ACButton from '../../atoms/ACButton';
import ACStatus from '../../atoms/ACStatus';
import ACUIElement from '../../atoms/ACUIElement';
import ACSaveBar from '../../atoms/ACSaveBar';
import ACSelect from '../../atoms/ACSelect';

import {
  getUIElementType,
  getUIElement,
  getMediaForUIName,
  getUIElementsWithMedias,
} from '../../common/global';

import styles from './VirtualDetails.module.scss';
import ThemeDesigner from 'components/common/ThemeDesigner';

// A virtual experience is connected to either a legacy product or an Occasions package
// component; the connection is what the planner keys off when it looks for the virtual
// experiences available to a booking. Separate axis from `type` ('Product' | 'Bundle').
// A virtual connects to a legacy product. Assigning an Occasions package component here has
// been retired: a component now names the virtuals that may deliver it, authored on the
// component's own page in the Hub and mirrored onto the virtual as `components`. That is
// many-to-many and coexists with the product connection, where this control was one virtual to
// one component and cleared the product. The same change the route editor got.

export default class VirtualDetails extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      virtual: props.virtual || {},
      updateData: {}, // This holds all the changes to be saved
      isCopyVirtualConfOpen: false,
      messages: [],
    };
  }

  componentDidUpdate(prevProps) {
    const { virtual = {}, media } = this.props;

    if (virtual && virtual !== prevProps.virtual) {
      if (virtual.id === '-1') {
        this.setState({ updateData: { status: 'active' } });
      } else {
        this.setState({ updateData: {} });
      }
      this.setState({ virtual });
    }

    if (!isEmpty(media) && media !== prevProps.media) {
      this.updateMedia(media);
    }
  }

  updateMedia(media) {
    const { virtual = {} } = cloneDeep(this.state);
    const { name, url } = media;
    let uiElement = {};
    let uiEleMedia = {};
    switch (name) {
      case 'introduction':
        uiElement = getUIElement(virtual.uiElements, getUIElementType().introduction);
        break;
      case 'ending':
        uiElement = getUIElement(virtual.uiElements, getUIElementType().ending);
        break;
      default:
        break;
    }
    uiEleMedia = uiElement.media || {};
    uiEleMedia.url = url;
    uiElement.media = uiEleMedia;
    this.updateVirtualData(cloneDeep(uiElement));
  }

  updateVirtualData(uiElement) {
    const { virtual = {}, updateData } = cloneDeep(this.state);
    let { uiElements = [] } = virtual;

    // uiElements = null does not get in destructuring
    uiElements = uiElements ? uiElements : [];
    const { type = '', subType = '' } = uiElement;

    const index = uiElements.findIndex((ele) => {
      if (!isEmpty(subType)) return ele.type === type && ele.subType === subType;
      else return ele.type === type;
    });
    if (index >= 0) uiElements[index] = uiElement;
    else uiElements.push(uiElement);

    virtual.uiElements = uiElements;
    updateData.uiElements = uiElements;

    this.setState({ virtual, updateData });
  }

  onChangeACUIElement = (uiElement) => {
    this.updateVirtualData(uiElement);
  };

  onChangeText = (event) => {
    const { name = '', value = '' } = event.target || {};
    if (isEmpty(name)) return;

    const { virtual, updateData } = cloneDeep(this.state);
    virtual[name] = value;
    updateData[name] = value;
    this.setState({ virtual, updateData });
  };

  onUploadACUIElementMedia = (name, s3FolderName, mediaType, file) => {
    const mediaFor = {
      ui: getMediaForUIName().virtual,
      name: name,
      s3FolderName: s3FolderName,
    };

    this.props.uploadMediaFunc(mediaFor, mediaType, file);
  };

  onChangeOwner = (name, selectedOption) => {
    const { value = '' } = selectedOption;
    if (isEmpty(name)) return;

    let { virtual, updateData } = cloneDeep(this.state);
    virtual[name] = value;
    updateData[name] = value;
    this.setState({ virtual, updateData });
  };

  /**
   * Keeps a connection's id and snapshot in step so the saved patch is unambiguous for
   * ac-experience's decorator. Only the product connection is authored here now; the component
   * links live in `virtual.components` and must never appear in a save from this screen.
   */
  setConnection = (connection) => {
    let { virtual, updateData } = cloneDeep(this.state);
    Object.entries(connection).forEach(([key, value]) => {
      virtual[key] = value;
      updateData[key] = value;
    });
    this.setState({ virtual, updateData });
  };

  onChangeProduct = (name, selectedOption) => {
    const { value = '' } = selectedOption;
    if (isEmpty(name)) return;

    // `connectionType`/`componentId`/`component` are the retired single connection. Still
    // cleared on a product save, so a virtual saved through this picker sheds the old one.
    this.setConnection({
      [name]: value,
      connectionType: 'product',
      componentId: null,
      component: null,
    });
    this.props.onChangeProductByIdFunc(value);
  };


  onInputChangeProduct = (query) => {
    const { onInputChangeProductFunc } = this.props;
    if (isEmpty(query)) return null;
    onInputChangeProductFunc(query);
  };

  onChangeNotes = (event) => {
    const { name = '', value = '' } = event.target || {};
    if (isEmpty(name)) return;

    let { virtual, updateData } = cloneDeep(this.state) || {};

    let { note = {} } = virtual;
    if (note === null) note = {};
    note[name] = value;
    virtual.note = note;
    updateData.note = note;
    this.setState({ virtual, updateData });
  };

  onCopyVirtual = () => {
    const { virtual } = cloneDeep(this.state);
    const { name = '' } = virtual;
    delete virtual.id;
    virtual.name = `Copy of ${name}`;
    this.props.onSaveVirtualFunc(virtual);
  };

  onSaveVirtual = () => {
    const { virtual, updateData } = cloneDeep(this.state);
    if (!isEmpty(virtual) && virtual.id !== '-1') updateData.id = virtual.id;
    const { uiElements = [] } = updateData;
    if (!isEmpty(uiElements)) updateData.uiElements = getUIElementsWithMedias(uiElements);
    this.props.onSaveVirtualFunc(updateData);
  };

  askCopyingVirtualConfirmation = () => {
    const { virtual } = cloneDeep(this.state);
    const { name = '' } = virtual;
    const messages = [`Are you sue you want to clone '${name}'?`];
    this.setState({ isCopyVirtualConfOpen: true, messages });
  };

  onClickCopyVirtualConfYes = () => {
    this.setState({ isCopyVirtualConfOpen: false, messages: [] });
    this.onCopyVirtual();
  };

  onClickCopyVirtualConfNo = () => {
    this.setState({ isCopyVirtualConfOpen: false, messages: [] });
  };

  onChangeTypeProduct = () => {
    const type = 'type';
    let { virtual, updateData } = cloneDeep(this.state);
    virtual[type] = 'Product';
    updateData[type] = 'Product';
    this.setState({ virtual, updateData });
  };

  onChangeTypeBundle = () => {
    const type = 'type';
    let { virtual, updateData } = cloneDeep(this.state);
    virtual[type] = 'Bundle';
    updateData[type] = 'Bundle';
    this.setState({ virtual, updateData });
  };
  
  onThemeUpdated(theme) {
    console.log({updateTheme : theme});
    let { virtual, updateData } = cloneDeep(this.state);
    virtual['theme'] = theme;
    updateData['theme'] = theme;
    this.setState({ virtual, updateData });
  }
  

  renderIntroduction() {
    const mediaFor = 'generic';
    const { virtual = {} } = cloneDeep(this.state);
    const { uiElements } = virtual;
    const introduction = getUIElement(uiElements, getUIElementType().introduction);
    const { media = {} } = introduction;
    if (isEmpty(media.type)) media.type = 'image';
    introduction.media = media;

    console.log('Introduction', introduction);
    return (
      <div className={styles.section}>
        <div className={styles.sectionHeading}>Introduction</div>

        <ACUIElement
          name="introduction"
          mediaFor={mediaFor}
          uiElement={introduction}
          // showMedia={false}
          onChangeACUIElementFunc={this.onChangeACUIElement}
          onUploadACUIElementMediaFunc={this.onUploadACUIElementMedia}
          isMediaTypeDisabled={true}
        />
      </div>
    );
  }

  renderEnding() {
    const mediaFor = 'generic';
    const { virtual = {} } = cloneDeep(this.state);
    const { uiElements } = virtual;
    const ending = getUIElement(uiElements, getUIElementType().ending);

    return (
      <div className={styles.section}>
        <div className={styles.sectionHeading}>Ending</div>
        {virtual.type==="Bundle" && <div className={styles.sectionHeading} style={{fontSize: '12px'}}>Leave below sections blank for default content</div>}
        <ACUIElement
          name="ending"
          mediaFor={mediaFor}
          uiElement={ending}
          onChangeACUIElementFunc={this.onChangeACUIElement}
          onUploadACUIElementMediaFunc={this.onUploadACUIElementMedia}
          placeholderHead={virtual.type==="Bundle"?"Thank You":""}
          placeholderSubhead={virtual.type==="Bundle"?"Want More ?":""}
          placeholderBody={virtual.type==="Bundle"?"You’re in luck! Explore many more Amazing experiences and WonderPass exclusives.":""}
        />
      </div>
    );
  }

  renderOwners() {
    const { virtual = { userId: -1 } } = cloneDeep(this.state);
    const { users = [] } = this.props;

    let { user = {} } = this.props;

    if (isEmpty(user) && !isEmpty(users)) {
      user =
        users.find((u) => {
          return u.id === virtual.userId;
        }) || user;
    }

    return (
      <ACSelect
        name="userId"
        isSelectWithToolTip={false}
        label="Owner"
        options={users}
        option={user}
        optionLabel={'name'}
        optionValue={'id'}
        onChangeFunc={this.onChangeOwner}
      />
    );
  }

  renderProductOrBundle() {
    let { virtual } = cloneDeep(this.state);
    console.log("virtual.type",virtual.type)
    return (
      <div className={styles.productBundle}>
            <ACButton
              text={'Product'}
              backgroundColor={virtual.type!=="Bundle"?"#D1517A":"#F1F1F1"}
              spanMargin="0 0"
              color={virtual.type!=="Bundle"?"#fff":"#A1A1A1"}
              border="1px solid #fff"              
              onClickFunc={this.onChangeTypeProduct}
              padding={virtual.type!=="Bundle"?'2px 16px 2px 16px':'2px 25px 2px 16px'}
              zIndex={virtual.type!=="Bundle"?"2":"1"}
            />
            <ACButton
              text={'Bundle Content'}
              backgroundColor={virtual.type==="Bundle"?"#D1517A":"#F1F1F1"}
              spanMargin="0 0"
              color={virtual.type==="Bundle"?"#fff":"#A1A1A1"}
              border="1px solid #fff"
              margin={'0 0 0 -20px'}
              onClickFunc={this.onChangeTypeBundle}
              padding={virtual.type==="Bundle"?'2px 16px 2px 16px':'2px 16px 2px 25px'}
              zIndex={virtual.type==="Bundle"?"2":"1"}
            />
          </div>      
    );
  }

  /**
   * The Occasions package components this virtual may deliver, read-only.
   *
   * Authored on the component's own page in the Hub, which writes both content-api's record and
   * this mirror; an editable copy here would give the pairing two writers. Names come from the
   * snapshot aex stores beside each id, so no content-api lookup is needed - a name that looks
   * out of date is refreshed by re-linking in the Hub.
   *
   * Absent when there are no links, so an ordinary product virtual looks exactly as it did.
   */
  renderComponents() {
    const { virtual = {} } = this.state;
    const components = (virtual.components || []).filter(Boolean);
    if (isEmpty(components)) return null;

    return (
      <TextField
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" className={styles.adornment}>
              Components
            </InputAdornment>
          ),
        }}
        value={components.map((c) => c.title || c.id).join(', ')}
        className={styles.textField}
        helperText="Delivered for these package components. Edit in the Hub, on the component."
        disabled
        multiline
      />
    );
  }

  renderProducts() {
    const { products = [], product = {} } = this.props;

    let options = [];

    // Just load top 50. Otherwise, it slows down the laoding of items.
    const maxOptions = 50;
    if (!isEmpty(products)) {
      options = products.slice(0, maxOptions).map((p) => {
        let toolTip = '';
        try {
          toolTip = `${p.name} (${p.productCities[0].city})`;
        } catch (err) {}

        return {
          label: p.name,
          value: p.id,
          toolTip: toolTip,
        };
      });
    }
    return (
      <ACSelect
        name="productId"
        isSelectWithToolTip={true}
        label="Product"
        options={cloneDeep(options)}
        option={product}
        optionLabel={'name'}
        optionValue={'id'}
        maxOptions={maxOptions}
        onChangeFunc={this.onChangeProduct}
        onInputChangeOptionFunc={this.onInputChangeProduct}
      />
    );
  }

  renderSuitabes() {
    const { virtual = {} } = this.props;
    const { suitable = {} } = virtual;

    return (
      <React.Fragment>
        <p className={styles.title}>Suitable for</p>
        <FormControlLabel
          control={<Checkbox color="primary" checked={suitable.adults || false} disabled />}
          label="Adults"
          key="adults"
        />
        <FormControlLabel
          control={<Checkbox color="primary" checked={suitable.children || false} disabled />}
          label="Children"
          key="children"
        />
        <FormControlLabel
          control={<Checkbox color="primary" checked={suitable.couples || false} disabled />}
          label="Couples"
          key="couples"
        />
      </React.Fragment>
    );
  }

  renderNotes() {
    const { virtual = {} } = cloneDeep(this.state);
    let { note = {}, notification = '' } = virtual;

    note = note === null ? {} : note;
    notification = notification === null ? '' : notification;

    return (
      <React.Fragment>
        <div className={styles.section}>
          Internal
          <TextField
            name="private"
            value={note.private || ''}
            className={styles.note}
            onChange={this.onChangeNotes}
            placeholder="Eg, if John isn't there ask for Claire."
          />
        </div>
        <div className={styles.section}>
          External Notes
          <TextField
            name="notification"
            value={notification}
            className={styles.note}
            onChange={this.onChangeText}
            placeholder={`Add route-specific notes or leave blank for default content "We recommend you start around 11 am to ensure the experience flows well"`}
          />
        </div>
      </React.Fragment>
    );
  }

  render() {
    const {
      virtual = {},
      updateData = {},
      isCopyVirtualConfOpen,
      messages,
    } = cloneDeep(this.state);
    
    if (isEmpty(virtual)) return <div />;

    const isEnabled = !isEmpty(updateData);
    const { name = '', status = '', theme } = virtual;
    return (
      <div className={styles.root}>
        <div className={styles.top}>
          <div className={styles.header}>
            Details
            <ACButton
              text={'Clone'}
              icon={<FileCopyIcon />}
              onClickFunc={this.askCopyingVirtualConfirmation}
            />
          </div>
          <div>
            <div className={styles.name}>
              <TextField
                name="name"
                value={name}
                className={styles.nameText}
                onChange={this.onChangeText}
                placeholder="Name"
              />
            </div>
            <ACStatus
              name={status}
              type="virtual"
              status={status}
              isEditable={true}
              onChangeFunc={this.onChangeText}
            />
          </div>
        </div>
        <div className={styles.bottom}>          
          {this.renderProductOrBundle()}
          {virtual.type !== 'Bundle' && this.renderProducts()}
          {virtual.type !== 'Bundle' && this.renderComponents()}
          {this.renderOwners()}
          <div className={styles.variable}>{this.renderSuitabes()}</div>
          {this.renderNotes()}
                <div className={styles.section}>
                    <ThemeDesigner theme={theme} onUpdateTheme={this.onThemeUpdated.bind(this)} />
                </div>
          {this.renderIntroduction()}
          {this.renderEnding()}
        </div>
        <ACSaveBar isEnabled={isEnabled} onSaveFunc={this.onSaveVirtual} />
        <ACDialog
          isOpen={isCopyVirtualConfOpen}
          heading="Confirmation"
          body={messages}
          okButtonLabel="Yes"
          cancelButtonLabel="No"
          onClickOkFunc={this.onClickCopyVirtualConfYes}
          onClickCancelFunc={this.onClickCopyVirtualConfNo}
          onClickACDialogClose={this.onClickCopyVirtualConfNo}
        />
      </div>
    );
  }
}
